/**
 * Utility functions for Probabilistic Forecast component
 */

/**
 * Process raw forecast data for chart consumption
 */
export const processChartData = (rawData, config) => {
  if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
    return null;
  }

  const { dataPointsLimit } = config.widget;
  const recentData = rawData.slice(-dataPointsLimit);

  return {
    timeAxis: recentData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }),
    actualPower: recentData.map(item => parseFloat(item.actual_power_kw)),
    p10: recentData.map(item => parseFloat(item.predicted_p10_kw)),
    p50: recentData.map(item => parseFloat(item.predicted_p50_kw)),
    p90: recentData.map(item => parseFloat(item.predicted_p90_kw)),
    sourceData: recentData,
  };
};

/**
 * Generate ECharts configuration
 */
export const generateChartOptions = (processedData, config) => {
  if (!processedData) return {};

  const { chart } = config;
  const { colors, tooltip } = chart;

  return {
    title: {
      text: chart.title.text,
      left: chart.title.left,
      top: chart.title.top,
      textStyle: {
        fontSize: chart.title.fontSize,
        fontWeight: chart.title.fontWeight
      }
    },
    legend: {
      data: chart.legend.data,
      top: chart.legend.top,
      right: chart.legend.right,
      selectedMode: chart.legend.selectedMode
    },
    tooltip: {
      trigger: tooltip.trigger,
      axisPointer: { type: tooltip.axisPointer.type },
      formatter: (params) => formatTooltip(params, processedData, config)
    },
    grid: chart.grid,
    xAxis: {
      type: 'category',
      data: processedData.timeAxis,
      name: chart.xAxis.name,
      nameLocation: chart.xAxis.nameLocation,
      nameGap: chart.xAxis.nameGap
    },
    yAxis: {
      type: 'value',
      name: chart.yAxis.name,
      nameLocation: chart.yAxis.nameLocation,
      nameGap: chart.yAxis.nameGap
    },
    series: generateSeriesConfig(processedData, config)
  };
};

/**
 * Generate series configuration for ECharts
 */
const generateSeriesConfig = (processedData, config) => {
  const { colors, barWidth } = config.chart;

  return [
    {
      name: 'Actual Power',
      type: 'bar',
      data: processedData.actualPower,
      itemStyle: { 
        color: colors.actual,
        opacity: colors.barOpacity || 1
      },
      barWidth: barWidth,
      z: 5
    },
    {
      name: 'Forecast Range',
      type: 'custom',
      renderItem: (params, api) => renderForecastRange(params, api, colors),
      data: processedData.timeAxis.map((time, index) => [
        time,
        processedData.p10[index],
        processedData.p90[index],
        processedData.p50[index]
      ]),
      z: 10,
    },
    // Hidden series for legend display
    { 
      name: 'P10 Forecast', 
      type: 'bar', 
      itemStyle: { color: colors.p10p90 },
      data: [] // Empty data to show only in legend
    },
    { 
      name: 'P50 Forecast', 
      type: 'bar', 
      itemStyle: { color: colors.p50 },
      data: [] // Empty data to show only in legend
    },
    { 
      name: 'P90 Forecast', 
      type: 'bar', 
      itemStyle: { color: colors.p10p90 },
      data: [] // Empty data to show only in legend
    },
  ];
};

/**
 * Custom renderer for forecast range visualization
 */
const renderForecastRange = (params, api, colors) => {
  const xVal = api.value(0);
  const p10Val = api.value(1);
  const p90Val = api.value(2);
  const p50Val = api.value(3);

  const p10Point = api.coord([xVal, p10Val]);
  const p90Point = api.coord([xVal, p90Val]);
  const p50Point = api.coord([xVal, p50Val]);

  if (!p10Point || !p90Point || !p50Point) return;

  const style = { stroke: colors.p50, lineWidth: 2 };
  const p50LineWidth = api.size([1, 0])[0] * 0.5;

  return {
    type: 'group',
    children: [
      {
        type: 'line',
        shape: {
          x1: p50Point[0],
          y1: p10Point[1],
          x2: p50Point[0],
          y2: p90Point[1]
        },
        style: style
      },
      {
        type: 'line',
        shape: {
          x1: p50Point[0] - p50LineWidth / 2,
          y1: p50Point[1],
          x2: p50Point[0] + p50LineWidth / 2,
          y2: p50Point[1]
        },
        style: style
      }
    ],
  };
};

/**
 * Format tooltip content
 */
const formatTooltip = (params, processedData, config) => {
  const { colors, tooltip } = config.chart;
  const index = params[0].dataIndex;
  const sourceItem = processedData.sourceData[index];
  const date = new Date(sourceItem.date);

  const getMarker = (color) => {
    return `<span style="display:inline-block;margin-right:5px;border-radius:3px;width:10px;height:10px;background-color:${color};"></span>`;
  };

  const formatDateTime = (date) => {
    const year = String(date.getFullYear()).slice(-2);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatValue = (value) => value.toFixed(tooltip.precision);

  return `<b>Power Forecast</b><br/>` +
         `${getMarker(colors.actual)}Actual : ${formatValue(processedData.actualPower[index])}${tooltip.unit}<br/>` +
         `${getMarker(colors.p10p90)}P10 : ${formatValue(processedData.p10[index])}${tooltip.unit}<br/>` +
         `${getMarker(colors.p50)}P50 : ${formatValue(processedData.p50[index])}${tooltip.unit}<br/>` +
         `${getMarker(colors.p10p90)}P90 : ${formatValue(processedData.p90[index])}${tooltip.unit}<br/>` +
         `${formatDateTime(date)}`;
};

/**
 * Format timestamp for display
 */
export const formatLastUpdated = () => {
  return new Date().toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

/**
 * Validate configuration object
 */
export const validateConfig = (config) => {
  const requiredPaths = [
    'widget.title',
    'widget.refreshInterval',
    'chart.colors.actual',
    'api.endpoint'
  ];

  for (const path of requiredPaths) {
    if (!getNestedValue(config, path)) {
      console.warn(`Missing required config value: ${path}`);
      return false;
    }
  }
  return true;
};

/**
 * Get nested object value by dot notation path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};
